//React
import React, { useEffect, useState, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import TopAudiences from "../../components/manager/audienceRow";
import SelectField from "../../components/inputs/select";
import BasicInput from "../../components/inputs/basic";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { FilterMatchMode } from "primereact/api";
import { Tooltip } from "react-tooltip";
import Popmenu from "../../components/header/popmenu";
import { getAllCohortsbyTagID, getCohortbyCohortID, updateCohort, isConcurrentChange } from "../../services/cohort";
import { getSession } from "../../services/auth";
import { isReadOnly as computeReadOnly, canManageLifecycleAndDelivery } from "../../utilities/permissions";
import { getTag } from "../../services/tag";
import { DV360_ACTIVATION_METHOD_ID } from "../../utilities/dv360Slots";
import { EQUATIV_ACTIVATION_METHOD_ID, isValidEquativPrice, formatEquativPrice } from "../../utilities/equativPrice";
//Assets
import chev from "../../assets/svgs/chevron-down-green.svg";
import editIcon from "../../assets/svgs/edit-green.svg";
import checkIcon from "../../assets/svgs/check-square.svg";
import xIcon from "../../assets/svgs/x-green.svg";

//CSS
import "./index.scss";
import "react-tooltip/dist/react-tooltip.css";
import { DeleteConfirmation } from "../../components/confirmation/confirmation";
import { ArchiveConfirmation } from "../../components/confirmation/archiveEdit";
import CopyCohort from "../../components/confirmation/copyCohort";
import { UpdateActivationMethods } from "../../components/confirmation/updateActivationMethods";
import { BulkTtlEdit } from "../../components/confirmation/bulkTtlEdit";
import { BulkExport, BulkImport } from "../../components/confirmation/bulkExportImport";
import Toast from "../../components/toast";
import 'primeicons/primeicons.css';

// How long the tab must have been hidden before coming back reloads the list (ASB-4801).
const RELOAD_AFTER_AWAY_MS = 5000;

// A save refused because someone else saved the audience after this list loaded (ASB-4801):
// say so in the API's words; the caller reloads the list so the next try sends the new version.
const concurrentChangeToast = (err) =>
	isConcurrentChange(err)
		? { status: "Invalid", title: "Not saved", message: `${err.data.message} The list has been reloaded.` }
		: null;

const tabs = ["All", "Auto-generated", "Custom", "Modeled", "Signal"];

// The pair of filters each tab sets. One table, read by handleTab, filteredCount and tabCounts
// alike, so a tab's count can never disagree with the rows that tab lists.
const tabFilters = {
	All: { autoGenerated: "", audience_type: "" },
	"Auto-generated": { autoGenerated: true, audience_type: "Auto-Generated" },
	Custom: { autoGenerated: false, audience_type: "Custom" },
	Modeled: { autoGenerated: false, audience_type: "Modeled" },
	Signal: { autoGenerated: false, audience_type: "Signal" },
};

// "" means the tab does not filter on that field -- how "All" is expressed, and why `false`
// cannot be tested for truthiness here. Strict, to match the table's FilterMatchMode.EQUALS.
const matchesTabFilter = (row, autoGenerated, audience_type) =>
	(audience_type === "" || audience_type == null || row.audience_type === audience_type) &&
	(autoGenerated === "" || autoGenerated == null || row.autoGenerated === autoGenerated);

// The paginator's page report, e.g. "1-10 of 1,197". A function template replaces PrimeReact's
// own span rather than wrapping it, so it carries the class; `first` arrives 1-based.
function pageReport(options) {
	if (options.totalRecords === 0) return null;
	return (
		<span className={options.className}>
			{options.first.toLocaleString()}–{options.last.toLocaleString()} of{" "}
			{options.totalRecords.toLocaleString()}
		</span>
	);
}

const users = [
	{ value: [0, 24999], label: "0-24.9K" },
	{ value: [25000, 49999], label: "25K-49K" },
	{ value: [50000, 99999], label: "50K-99K" },
	{ value: [100000, 249999], label: "100K-249K" },
	{ value: [250000, 499999], label: "250K-499K" },
	{ value: [500000, 999999], label: "500K-999K" },
	{ value: [1000000, 100000000], label: "1M+" },
];
const prebidCPM = [
	{ value: [0.0, 0.99], label: "$0.00 - $0.99" },
	{ value: [1.0, 1.99], label: "$1.00 - $1.99" },
	{ value: [2.0, 2.99], label: "$2.00 - $2.99" },
	{ value: [3.0, 3.99], label: "$3.00 - $3.99" },
	{ value: [4.0, 4.99], label: "$4.00 - $4.99" },
	{ value: [5.0, 1000], label: "$5.00+ " },
];
const activeStatus = [
	{ value: true, label: "Active", tooltip: "Audience that is active, populating and ready for use" },
	{ value: "pending", label: "Model Pending", tooltip: "Model generation in progress"},
	{ value: "error", label: "Model Error", tooltip: "Model generation error. Please reach out to your ArcSpan representative."},
	{ value: false, label: "Archived", tooltip: "Audience has been archived and should not be used" }
];

const initialFilters = {
	unique_users: { value: null, label: "", matchMode: FilterMatchMode.BETWEEN },
	prebid_cpm: { value: null, label: "", matchMode: FilterMatchMode.BETWEEN },
	active: { value: "", label: "", matchMode: FilterMatchMode.EQUALS },
	audience_name: { value: "", label: "", matchMode: FilterMatchMode.CONTAINS },
	audience_name_search_box: { value: "", label: "", matchMode: FilterMatchMode.CONTAINS },
	autoGenerated: { value: "", label: "", matchMode: FilterMatchMode.EQUALS },
	audience_type: { value: "", label: "", matchMode: FilterMatchMode.EQUALS },
};
const headers = [
	["date_created", "Date Created"],
	["audience_name", "Audience Name"],
	["audience_type", "Audience Type"],
	["unique_users", "Unique Users"],
	["prebid_cpm", "Prebid CPM"],
	["prebid_revenue", "Prebid Revenue"],
	["active", "Status"],
];

export default function Manager({}) {
	const navigate = useNavigate();
	const [tagId, setTagId] = useState(localStorage.getItem("activeSilo"));
	const [publisherSettings, setPublisherSettings] = useState(localStorage.getItem("publisherSettings"));
	const [popMenus, setPopMenus] = useState(false);
	const [currentTab, setTab] = useState(tabs[0]);
	const [selectedRows, setSelectedRows] = useState([]);
	const [loading, setLoading] = useState(true);
	const [filters, setFilters] = useState(initialFilters);
	const [tableState, setTableState] = useState(null);
	const [disabledEditBtn, setDisabledEditBtn] = useState(false);
	const [disabledViewBtn, setDisabledViewBtn] = useState(false);
	const [disabledCopyBtn, setDisabledCopyBtn] = useState(false);
	const [disabledDeleteBtn, setDisabledDeleteBtn] = useState(false);
	const [disabledArchiveBtn, setDisabledArchiveBtn] = useState(false);
	const [disabledUnarchiveBtn, setDisabledUnarchiveBtn] = useState(false);
	const [disabledExportBtn, setDisabledExportBtn] = useState(false);
	const [disabledPushCohortsBtn, setDisabledPushCohortsBtn] = useState(false);
	const [disabledPullCohortsBtn, setDisabledPullCohortsBtn] = useState(false);
	const [disabledBulkTtlBtn, setDisabledBulkTtlBtn] = useState(false);
	const [showBulkTtlEdit, setShowBulkTtlEdit] = useState(false);
	const [cohortsToBulkTtlEdit, setCohortsToBulkTtlEdit] = useState(null);
	const [disabledBulkExportBtn, setDisabledBulkExportBtn] = useState(false);
	const [showBulkExport, setShowBulkExport] = useState(false);
	const [showBulkImport, setShowBulkImport] = useState(false);
	const [cohortsToBulkExport, setCohortsToBulkExport] = useState(null);
	const [audienceId, setAudienceId] = useState(null);
	const [selectedAudienceType, setSelectedAudienceType] = useState(null);
	const [showConfirmation, setShowConfirmation] = useState(false);
	const [archiveType, setArchiveType] = useState(null);
	const [showArchiveEdit, setShowArchiveEdit] = useState(false); // -- -- //
	const [showPushCohorts, setShowPushCohorts] = useState(false);
	const [showPullCohorts, setShowPullCohorts] = useState(false);
	const [showCopy, setShowCopy] = useState(false);
	const [cohortsToUpdate, setCohortsToUpdate] = useState(null);
	const [cohortsToDelete, setCohortsToDelete] = useState(null);
	const [cohortsToPush, setCohortsToPush] = useState(null);
	const [cohortsToPull, setCohortsToPull] = useState(null);
	const [modelingEnabled, setModelingEnabled] = useState(false);
	const [signalEnabled, setSignalEnabled] = useState(false);
	const [amsFlavor, setAmsFlavor] = useState("");
	const [bulkEditEnabled, setBulkEditEnabled] = useState(false);
	const [isAdmin, setIsAdmin] = useState(false);
	const [readOnly, setReadOnly] = useState(false);
	// Publisher Contributor (role 5) is an editor (readOnly=false) but may not delete,
	// archive/activate, or push/pull to destinations. See ASB-3200.
	const [canManageLifecycle, setCanManageLifecycle] = useState(false);
	const [value, setValue] = useState({
		unique_users: null,
		prebid_cpm: null,
		active: null,
	});
	const [selectedOptions, setSelectedOptions] = useState({
		unique_users: null,
		prebid_cpm: null,
		active: null,
	});
	const [currency, setCurrency] = useState("USD");
	const [prebidCpmOptions, setPrebidCpmOptions] = useState([]);
	const [tagData, setTagData] = useState(null);
	const [publisherName, setPublisherName] = useState(null);
	const [editingId, setEditingId] = useState(null);
	const [editingName, setEditingName] = useState("");
	const [editingSaving, setEditingSaving] = useState(false);
	// The row's version when its editor opened (ASB-4801): a reload while the editor is open
	// brings in someone else's version, and saving with that would overwrite their change.
	const [editingVersion, setEditingVersion] = useState(null);
	const [editToast, setEditToast] = useState(null);
	const [editingPriceId, setEditingPriceId] = useState(null);
	const [editingPrice, setEditingPrice] = useState("");
	const [editingPriceSaving, setEditingPriceSaving] = useState(false);
	const [editingPriceError, setEditingPriceError] = useState(null);
	const [editingPriceVersion, setEditingPriceVersion] = useState(null);

	let popRef = useRef();

	useEffect(() => {
		window.addEventListener("storage", () => {
			setTagId(localStorage.getItem("activeSilo"));
			setPublisherSettings(localStorage.getItem("publisherSettings"));
		});
		tagCohorts(tagId);
	}, [tagId, publisherSettings]);

	// ASB-4801: Edit opens an audience in a new tab, and a save there moves its version. Coming
	// back to this page after a while reloads the list, or the next action here would send the
	// old version and be refused as someone else's change. "Away" is a hidden tab or, for a
	// second window side by side, a window without focus. A quick switch doesn't reload.
	useEffect(() => {
		let awayAt = null;
		const leave = () => {
			if (awayAt == null) awayAt = Date.now();
		};
		const back = () => {
			if (awayAt != null && Date.now() - awayAt >= RELOAD_AFTER_AWAY_MS) tagCohorts(tagId);
			awayAt = null;
		};
		const onVisibility = () => (document.visibilityState === "hidden" ? leave() : back());
		document.addEventListener("visibilitychange", onVisibility);
		window.addEventListener("blur", leave);
		window.addEventListener("focus", back);
		return () => {
			document.removeEventListener("visibilitychange", onVisibility);
			window.removeEventListener("blur", leave);
			window.removeEventListener("focus", back);
		};
	}, [tagId, publisherSettings]);

	const formatDate = (date) => {
		const format = rawDate(date);
		return format.toLocaleDateString();
	};

	const rawDate = (date) => {
		const formattedDate = Date.parse(date.replace("PM", "").replace("AM", ""));
		return new Date(formattedDate);
	};

	const cohortStatusIcon = (cohort) => {
		if (cohort.active && cohort.modeled_info && ["created", "started"].includes(cohort.modeled_info.status)) {
			return "pi-clock pending";
		} else if (cohort.active && cohort.modeled_info && cohort.modeled_info.status === "error") {
			return "pi-exclamation-circle error";
		} else {
			return cohort.active ? "pi-circle-fill active" : "pi-circle-fill archived";
		}
	}

	const cohortStatusLabel = (cohort) => {
		if (cohort.active && cohort.modeled_info && ["created", "started"].includes(cohort.modeled_info.status)) {
			return activeStatus.filter((e) => e.value === "pending")[0].label;
		} else if (cohort.active && cohort.modeled_info && cohort.modeled_info.status === "error") {
			return activeStatus.filter((e) => e.value === "error")[0].label;
		} else {
			return activeStatus.filter((e) => e.value === cohort.active)[0].label;
		}
	}

	const cohortStatusTooltip = (cohort) => {
		if (cohort.active && cohort.modeled_info && ["created", "started"].includes(cohort.modeled_info.status)) {
			return activeStatus.filter((e) => e.value === "pending")[0].tooltip;
		} else if (cohort.active && cohort.modeled_info && cohort.modeled_info.status === "error") {
			return activeStatus.filter((e) => e.value === "error")[0].tooltip;
		} else {
			return activeStatus.filter((e) => e.value === cohort.active)[0].tooltip;
		}
	}

	// Reloads can overlap (a return reload, then a save's own); only the newest may fill the
	// table, or an older one landing late puts back versions a save has moved (ASB-4801).
	const loadSeq = useRef(0);
	const tagCohorts = async (tagId) => {
		const session = getSession();
		if (!session) return;
		const seq = ++loadSeq.current;

		const data = await getTag(tagId);
		setTagData(data.data);
		setPublisherName(data.data.publisher_name);

		const settings = await JSON.parse(publisherSettings) ?? {};
		let modelingEnabledValue = settings.modeling_enabled ?? false;
		let amsFlavorValue = settings.ams_flavor ?? "full";
		let signalEnabledValue = settings.signalEnabled ?? false;
		setModelingEnabled(modelingEnabledValue);
		setSignalEnabled(signalEnabledValue);
		setAmsFlavor(amsFlavorValue);
		setBulkEditEnabled(settings.bulkEditEnabled ?? false);
		const currencyValue = settings.display_currency ? settings.display_currency.symbol : "USD";
		setCurrency(currencyValue);
		const currencyFormatter = Intl.NumberFormat("en-Us", { style: "currency", currency: currencyValue, maximumFractionDigits: 2 });
		setPrebidCpmOptions(prebidCPM.map((el, index) => ({
			value: el.value,
			label: index !== prebidCPM.length - 1
				? `${currencyFormatter.format(el.value[0])} - ${currencyFormatter.format(el.value[1])}` : `${currencyFormatter.format(el.value[0])}+`
		})));
		setReadOnly(computeReadOnly(session.role_id));
		setCanManageLifecycle(canManageLifecycleAndDelivery(session.role_id));
		// Admins (role_id 1) always get the bulk editor; everyone else is gated on the publisher flag.
		setIsAdmin(session.role_id === 1);
		const exchangeRate = settings.display_currency ? settings.display_currency.exchangeRate : 1.0;
		await getAllCohortsbyTagID(tagId, amsFlavorValue, modelingEnabledValue, signalEnabledValue).then((cohortData) => {
				const cohorts = Object.entries(cohortData.data);
				const cohort = cohorts.map((cohort) => ({
					audience_name: cohort[1].name,
					audience_description: cohort[1].description,
					audience_name_search_box: `${cohort[1].name}|${cohort[1].unique_id}`,
					audience_type: audienceType(cohort[1]),
					date_created: formatDate(cohort[1].createdon),
					date_created_raw: rawDate(cohort[1].createdon),
					prebid_cpm: cohort[1].metrics.last30Days.averagePrebidCpm * exchangeRate,
					prebid_revenue: cohort[1].metrics.last30Days.prebidRevenue * exchangeRate,
					unique_users: cohort[1].metrics.last30Days.uniqueUsers,
					active: cohortStatusLabel(cohort[1]),
					id: cohort[1].cid,
					uniqueId: cohort[1].unique_id,
					// Sent back with the Manager's saves, so one made by someone else since this
					// list loaded is refused rather than overwritten (ASB-4801).
					version: cohort[1].version,
					autoGenerated: cohort[1].auto_generated,
					activation_methods: cohort[1].activation_methods ?? [],
					dv360Slot: (cohort[1].activation_methods ?? []).find((m) => m.id === DV360_ACTIVATION_METHOD_ID)?.slot ?? null,
					equativPrice: (cohort[1].activation_methods ?? []).find((m) => m.id === EQUATIV_ACTIVATION_METHOD_ID)?.details?.equativ?.price ?? null,
					info: (
						<div>
							<div style={{ display: 'flex', flexDirection: 'row', columnGap: '5px' }}>
								<div
									className="p-column-header-content"
									data-tooltip-id={`audience-name-${kebabCase("Status")}`}
									data-tooltip-content={cohortStatusTooltip(cohort[1])}
								>
									<i className={`pi ${cohortStatusIcon(cohort[1])}`} />
								</div>
								<div
									className="p-column-header-content"
									data-tooltip-id={`audience-name-${kebabCase("Audience Type")}`}
									data-tooltip-content={audienceTypeTooltip(cohort[1])}
								>
									<div className="box">
										<i className={`pi pi-circle-fill type`} />
										<div className="letter">
											{audienceTypeLetter(cohort[1])}
										</div>
									</div>
								</div>
								{cohort[1].signal_conditions && cohort[1].signal_conditions.affinity_score && (
									<div
										className="p-column-header-content"
										data-tooltip-id={`audience-name-${kebabCase("Affinity")}`}
										data-tooltip-content={"How much more likely this audience is to visit a page than the site average. 100 is the site baseline."}
									>
										<div className="box">
											<div className="score">
												{`${cohort[1].signal_conditions.affinity_score}+`}
											</div>
										</div>
									</div>
								)}
								{cohort[1].modeled_conditions && cohort[1].modeled_conditions.propensity_score && (
									<div
										className="p-column-header-content"
										data-tooltip-id={`audience-name-${kebabCase("Propensity Score")}`}
										data-tooltip-content={"A probability score that reflects how closely a user resembles users in the seed set"}
									>
										<div className="box">
											<div className="score">
												{`${cohort[1].modeled_conditions.propensity_score * 100}+`}
											</div>
										</div>
									</div>
								)}
							</div>
						</div>
					)
				})).filter((cohort) => {
					// Two independent switches: a publisher can have signal audiences without
					// modeling, and vice versa.
					if (cohort.audience_type === 'Modeled') {
						return amsFlavorValue === "full" && modelingEnabledValue;
					}
					if (cohort.audience_type === 'Signal') {
						return signalEnabledValue;
					}
					return true;
				});
				if (seq !== loadSeq.current) return;
				setLoading(false);
				return setTableState(cohort);
			});
	};

	const audienceType = (cohort) => {
		if (cohort.auto_generated === true) {
			return "Auto-Generated";
		} else if (cohort.cohort_type === "composite") {
			return "Custom";
		} else if (cohort.cohort_type === "modeled") {
			return "Modeled";
		} else if (cohort.cohort_type === "signal") {
			return "Signal";
		}
	};

	const audienceTypeTooltip = (cohort) => {
		if (cohort.auto_generated === true) {
			return "Auto-generated audience preconfigured by ArcSpan";
		} else if (cohort.cohort_type === "composite") {
			return "Custom audience created in the builder";
		} else if (cohort.cohort_type === "modeled") {
			return "Modeled audience scaled from a seed set";
		} else if (cohort.cohort_type === "signal") {
			return "Signal audience built from affinity to a base audience";
		}
	}

	const audienceTypeLetter = (cohort) => {
		if (cohort.auto_generated === true) {
			return "A";
		} else if (cohort.cohort_type === "composite") {
			return "C";
		} else if (cohort.cohort_type === "modeled") {
			return "M";
		} else if (cohort.cohort_type === "signal") {
			return "S";
		}
	}

	useEffect(() => {
		let handler = (event) => {
			if (!popRef.current?.contains(event.target)) {
				setPopMenus(false);
			}
		};
		document.body.addEventListener("click", handler);

		return () => {
			document.body.removeEventListener("click", handler);
		};
	});

	useEffect(() => {
		document.title = "Audience Manager";
	}, [filters]);

	useEffect(() => {}, [selectedRows]);

	const usersRef = useRef();
	const cpmRef = useRef();
	const statusRef = useRef();
	const audienceRef = useRef();
	const destinationRef = useRef();

	const [selectedDestination, setSelectedDestination] = useState(null);

	const destinationOptions = useMemo(() => {
		const map = new Map();
		for (const row of tableState ?? []) {
			for (const m of row.activation_methods ?? []) {
				if (!map.has(m.id)) map.set(m.id, { label: m.name, value: m.id });
			}
		}
		return [...map.values()].sort((a, b) => a.label.localeCompare(b.label));
	}, [tableState]);

	const displayedTableState = useMemo(() => {
		if (selectedDestination == null || tableState == null) return tableState;
		return tableState.filter((row) =>
			(row.activation_methods ?? []).some((m) => m.id === selectedDestination)
		);
	}, [tableState, selectedDestination]);

	// The DV360 Slot column only makes sense once the list is already scoped to DV360
	// audiences via the Destination filter — it stays hidden otherwise rather than
	// showing a mostly-empty column for every other destination. See ASB-3580.
	const showDv360SlotColumn = useMemo(() => {
		const dv360Option = destinationOptions.find((opt) => opt.value === DV360_ACTIVATION_METHOD_ID);
		return dv360Option != null && selectedDestination === dv360Option.value;
	}, [destinationOptions, selectedDestination]);

	// Same conditional-visibility rule as the DV360 Slot column above, for Equativ's
	// per-audience price (ASB-3872).
	const showEquativPriceColumn = useMemo(() => {
		const equativOption = destinationOptions.find((opt) => opt.value === EQUATIV_ACTIVATION_METHOD_ID);
		return equativOption != null && selectedDestination === equativOption.value;
	}, [destinationOptions, selectedDestination]);

	const filteredCount = useMemo(() => {
		if (!displayedTableState) return 0;
		const search = filters.audience_name_search_box?.value?.toString().toLowerCase() ?? "";
		return displayedTableState.filter((row) => {
			if (filters.unique_users?.value) {
				const [min, max] = filters.unique_users.value;
				if (row.unique_users < min || row.unique_users > max) return false;
			}
			if (filters.prebid_cpm?.value) {
				const [min, max] = filters.prebid_cpm.value;
				if (row.prebid_cpm < min || row.prebid_cpm > max) return false;
			}
			if (filters.active?.value) {
				if (row.active !== filters.active.value) return false;
			}
			if (!matchesTabFilter(row, filters.autoGenerated?.value, filters.audience_type?.value)) {
				return false;
			}
			if (search) {
				const v = row.audience_name_search_box;
				if (v == null || !String(v).toLowerCase().includes(search)) return false;
			}
			return true;
		}).length;
	}, [displayedTableState, filters]);

	// Each tab's own total, before any filter or search -- unlike filteredCount, which is what
	// the current tab shows.
	const tabCounts = useMemo(() => {
		const rows = tableState ?? [];
		return Object.fromEntries(
			tabs.map((tab) => {
				const { autoGenerated, audience_type } = tabFilters[tab];
				return [tab, rows.filter((row) => matchesTabFilter(row, autoGenerated, audience_type)).length];
			})
		);
	}, [tableState]);

	function filter(arr, filter) {
		setFilters((prevState) => ({
			...prevState,
			[filter]: { value: arr.value, matchMode: filters[filter].matchMode },
		}));
		setSelectedOptions((prevState) => ({
			...prevState,
			[filter]: arr,
		}));
	}

	function clearFilters() {
		setFilters(initialFilters);
		setSelectedOptions({
			unique_users: { value: null, label: "" },
			prebid_cpm: null,
			status: null,
		});
		setSelectedDestination(null);
		usersRef.current.clearSelect();
		cpmRef.current?.clearSelect();
		statusRef.current.clearSelect();
		destinationRef.current?.clearSelect();
		audienceRef.current.clearInput();
		handleTab(currentTab);
	}

	const formatUniqueUsers = (aud) => {
		let user = aud.unique_users;
		const formatter = Intl.NumberFormat("en-Us", { notation: "compact" });
		return formatter.format(user);
	};

	const formatPrebidCpm = (aud) => {
		const formatter = Intl.NumberFormat("en-Us", { style: "currency", currency: currency, maximumFractionDigits: 2 })
		return formatter.format((Math.round(aud.prebid_cpm * 100) / 100).toFixed(2));
	};

	const formatPrebidRevenue = (aud) => {
		const formatter = Intl.NumberFormat("en-Us", { style: "currency", currency: currency, maximumFractionDigits: 0 })
		return formatter.format((Math.round(aud.prebid_revenue * 100) / 100).toFixed(2));
	};

	const kebabCase = (string) =>
		string
			.replace(/([a-z])([A-Z])/g, "$1-$2")
			.replace(/[\s_]+/g, "-")
			.toLowerCase();

	const startEditing = (audience) => {
		setEditingId(audience.uniqueId);
		setEditingName(audience.audience_name);
		setEditingVersion(audience.version ?? null);
	};

	const cancelEditing = () => {
		setEditingId(null);
		setEditingName("");
		setEditingSaving(false);
		setEditingVersion(null);
	};

	const handleSaveName = async (audience) => {
		const trimmed = editingName.trim();
		if (!trimmed || trimmed === audience.audience_name) {
			cancelEditing();
			return;
		}

		setEditingSaving(true);
		try {
			const data = await getCohortbyCohortID(audience.uniqueId, tagId);
			const cohortData = data.data;
			cohortData.name = trimmed;
			// The version the row had when the editor opened, not the re-read's or a later
			// reload's: a save made since then is refused rather than overwritten (ASB-4801).
			if (editingVersion != null) cohortData.version = editingVersion;
			const payload = { data: [cohortData] };
			const saved = await updateCohort(payload, audience.uniqueId, tagId).then((r) => r.json());

			setTableState((prev) =>
				prev.map((row) =>
					row.uniqueId === audience.uniqueId
						? {
							...row,
							audience_name: trimmed,
							audience_name_search_box: `${trimmed}|${row.uniqueId}`,
							version: saved?.version ?? row.version,
						}
						: row
				)
			);
			cancelEditing();
			tagCohorts(tagId);
		} catch (err) {
			setEditingSaving(false);
			setEditToast(concurrentChangeToast(err) || {
				status: "Invalid",
				title: "Error",
				message: "Failed to rename audience. Please try again.",
			});
			// Close the editor so the cell shows the name the reload brings in, not the
			// user's text over the other person's change.
			if (isConcurrentChange(err)) {
				cancelEditing();
				tagCohorts(tagId);
			}
		}
	};

	const startEditingPrice = (audience) => {
		setEditingPriceId(audience.uniqueId);
		setEditingPrice(audience.equativPrice != null ? String(audience.equativPrice) : "");
		setEditingPriceError(null);
		setEditingPriceVersion(audience.version ?? null);
	};

	const cancelEditingPrice = () => {
		setEditingPriceId(null);
		setEditingPrice("");
		setEditingPriceSaving(false);
		setEditingPriceError(null);
		setEditingPriceVersion(null);
	};

	const handleSaveEquativPrice = async (audience) => {
		// Blank means "leave it as-is" (matches the name-edit field's own convention:
		// blank/unchanged silently cancels). A non-blank but invalid value is different —
		// silently discarding it left the user with no idea why their edit didn't stick, so
		// show an inline error and keep editing open instead.
		if (editingPrice === "") {
			cancelEditingPrice();
			return;
		}
		if (!isValidEquativPrice(editingPrice)) {
			setEditingPriceError("Enter a non-negative number with up to 2 decimal places.");
			return;
		}
		const newPrice = parseFloat(editingPrice);
		if (newPrice === audience.equativPrice) {
			cancelEditingPrice();
			return;
		}

		setEditingPriceSaving(true);
		try {
			// Minimal partial payload — PUT /cohorts/<uid>'s applyCohortUpdate/applyAutogenOverride
			// read each field as data.get(x, <current value>), so an absent key leaves it
			// untouched (name, conditions, other destinations, ...); confirmed by
			// arcspan_sandbox's test_equativ_price.py::test_omitting_price_leaves_it_unchanged.
			const saved = await updateCohort(
				{ data: [{ equativPrice: newPrice, ...(editingPriceVersion != null && { version: editingPriceVersion }) }] },
				audience.uniqueId,
				tagId
			).then((r) => r.json());

			setTableState((prev) =>
				prev.map((row) =>
					row.uniqueId === audience.uniqueId
						? { ...row, equativPrice: newPrice, version: saved?.version ?? row.version }
						: row
				)
			);
			cancelEditingPrice();
			// As after a rename: a newer load, so one already running can't put the old version back.
			tagCohorts(tagId);
		} catch (err) {
			setEditingPriceSaving(false);
			setEditToast(concurrentChangeToast(err) || {
				status: "Invalid",
				title: "Error",
				message: "Failed to update Equativ price. Please try again.",
			});
			// As for a rename: close the editor so the reloaded price shows.
			if (isConcurrentChange(err)) {
				cancelEditingPrice();
				tagCohorts(tagId);
			}
		}
	};

	const equativPriceBody = (audience) => {
		const isEditing = editingPriceId === audience.uniqueId;
		// The API always returns a real price now (cohorts.equativ_price is NOT NULL
		// DEFAULT 1.00, ASB-3872) — no client-side default fallback needed.
		const displayPrice = audience.equativPrice;

		if (isEditing) {
			return (
				<div>
					<div className="inline-edit-cell">
						<input
							className="inline-edit-input"
							type="text"
							inputMode="decimal"
							value={editingPrice}
							onChange={(e) => {
								setEditingPrice(e.target.value);
								if (editingPriceError) setEditingPriceError(null);
							}}
							onKeyDown={(e) => {
								if (e.key === "Enter") handleSaveEquativPrice(audience);
								if (e.key === "Escape") cancelEditingPrice();
							}}
							disabled={editingPriceSaving}
							autoFocus
						/>
						{editingPriceSaving ? (
							<i className="pi pi-spin pi-spinner inline-edit-spinner" />
						) : (
							<>
								<button
									className="inline-edit-btn"
									onClick={() => handleSaveEquativPrice(audience)}
									title="Save"
								>
									<img src={checkIcon} alt="save" />
								</button>
								<button
									className="inline-edit-btn"
									onClick={cancelEditingPrice}
									title="Cancel"
								>
									<img src={xIcon} alt="cancel" />
								</button>
							</>
						)}
					</div>
					{editingPriceError && <div className="p5">{editingPriceError}</div>}
				</div>
			);
		}

		return (
			<div className="inline-edit-cell">
				<span>{formatEquativPrice(displayPrice)}</span>
				{canManageLifecycle && (
					<button
						className="inline-edit-btn inline-edit-pencil"
						onClick={() => startEditingPrice(audience)}
						title="Edit Equativ Price"
					>
						<img src={editIcon} alt="edit" />
					</button>
				)}
			</div>
		);
	};

	const getViewPath = (audience) => {
		if (audience.audience_type === "Modeled") {
			return `/audience-modeling/view/${audience.uniqueId}`;
		}
		if (audience.audience_type === "Signal") {
			return `/audience-signal/view/${audience.uniqueId}`;
		}
		return `/audience-builder-v2/view/${audience.uniqueId}`;
	};

	const audienceNameBody = (audience) => {
		const isEditing = editingId === audience.uniqueId;
		const audienceAdjustedName = audience.audience_name.replaceAll("_", " ");

		if (isEditing) {
			return (
				<div className="inline-edit-cell">
					<input
						className="inline-edit-input"
						value={editingName}
						onChange={(e) => setEditingName(e.target.value)}
						onKeyDown={(e) => {
							if (e.key === "Enter") handleSaveName(audience);
							if (e.key === "Escape") cancelEditing();
						}}
						disabled={editingSaving}
						autoFocus
					/>
					{editingSaving ? (
						<i className="pi pi-spin pi-spinner inline-edit-spinner" />
					) : (
						<>
							<button
								className="inline-edit-btn"
								onClick={() => handleSaveName(audience)}
								title="Save"
							>
								<img src={checkIcon} alt="save" />
							</button>
							<button
								className="inline-edit-btn"
								onClick={cancelEditing}
								title="Cancel"
							>
								<img src={xIcon} alt="cancel" />
							</button>
						</>
					)}
				</div>
			);
		}

		return (
			<>
				<Tooltip id={`audience-name-${audience.uniqueId}`} />
				<div className="inline-edit-cell">
					<button
						type="button"
						className="audience-name-link"
						onClick={() => window.open(getViewPath(audience), "_blank")}
						data-tooltip-id={`audience-name-${audience.uniqueId}`}
						data-tooltip-content={`${audience.audience_name}${audience.audience_description ? ` (${audience.audience_description})` : ""}`}
						style={{ overflow: "hidden", textOverflow: "ellipsis", flex: 1 }}
					>
						{audienceAdjustedName}
					</button>
					{!readOnly && (
						<button
							className="inline-edit-btn inline-edit-pencil"
							onClick={() => startEditing(audience)}
							title="Rename"
						>
							<img src={editIcon} alt="edit" />
						</button>
					)}
				</div>
			</>
		);
	};

	function headerTemplate(val, tip) {
		return (
			<>
				{/* Tooltips created below the data table to prevent them from being trapped within data table. */}
				{/* This was a consequence of adding resizable columns and corresponding CSS. */}
				<div
					className="p-column-header-content"
					data-tooltip-id={`audience-name-${kebabCase(val)}`}
					data-tooltip-content={tip}
				>
					{val}
				</div>
			</>
		);
	}

	// The row's version as the table holds it now: an inline rename or price change updates
	// the table's row, not the copy the selection kept when the box was ticked (ASB-4801).
	const currentVersion = (row) => (tableState ?? []).find((r) => r.uniqueId === row.uniqueId)?.version ?? row.version;

	function handlePushCohorts() {
		const cohortIds = selectedRows.map((row) => ({
			value: row.uniqueId,
			label: row.audience_name,
			version: currentVersion(row),
		}));
		setCohortsToPush(cohortIds);
		setShowPushCohorts(!showPushCohorts);
	}

	function handlePullCohorts() {
		const cohortIds = selectedRows.map((row) => ({
			value: row.uniqueId,
			label: row.audience_name,
			version: currentVersion(row),
		}));
		setCohortsToPull(cohortIds);
		setShowPullCohorts(!showPullCohorts);
	}

	function handleDelete() {
		const cohortIds = selectedRows.map((row) => ({
			value: row.uniqueId,
			label: row.audience_name,
		}));
		setCohortsToDelete(cohortIds);
		setShowConfirmation(!showConfirmation);
	}

	function handleArchive() {
		const cohortIds = selectedRows.map((row) => ({
			value: row.uniqueId,
			label: row.audience_name,
			version: currentVersion(row),
		}));
		setCohortsToUpdate(cohortIds);
		setShowArchiveEdit(true);
	}
	function handleUnarchive() {
		const cohortIds = selectedRows.map((row) => ({
			value: row.uniqueId,
			label: row.audience_name,
			version: currentVersion(row),
		}));
		setCohortsToUpdate(cohortIds);
		setShowArchiveEdit(true);
	}
	function handleBulkExport() {
		const cohortIds = selectedRows.map((row) => ({
			value: row.uniqueId,
			label: row.audience_name,
		}));
		setCohortsToBulkExport(cohortIds);
		setShowBulkExport(true);
	}

	function handleBulkImport() {
		// Import is selection-independent — keyed off the signed keys inside the file.
		setShowBulkImport(true);
	}

	function handleBulkTtlEdit() {
		const cohortIds = selectedRows.map((row) => ({
			value: row.uniqueId,
			label: row.audience_name,
			version: currentVersion(row),
		}));
		setCohortsToBulkTtlEdit(cohortIds);
		setShowBulkTtlEdit(true);
	}

	function handleCopy() {
		const cohortIds = selectedRows.map((row) => ({
			value: row.uniqueId,
			label: row.audience_name,
		}));
		setCohortsToUpdate(cohortIds);
		setShowCopy(true);
	}

	const handleActions = (selectedRows) => {
		if (selectedRows.length === 1) {
			setAudienceId(selectedRows[0].uniqueId);
			setSelectedAudienceType(selectedRows[0].audience_type);
			setDisabledViewBtn(false);
			setDisabledCopyBtn(false);
			setDisabledEditBtn(false);
			setDisabledArchiveBtn(false);
			setDisabledUnarchiveBtn(false);
			const isAutoGenerated = selectedRows[0].autoGenerated;
			setDisabledDeleteBtn(isAutoGenerated);
			setDisabledExportBtn(false);
			setDisabledPushCohortsBtn(false);
			setDisabledPullCohortsBtn(false);
			setDisabledBulkTtlBtn(false);
		} else {
			setDisabledViewBtn(true);
			setDisabledCopyBtn(true);
			setDisabledEditBtn(true);
			setDisabledArchiveBtn(false);
			setDisabledUnarchiveBtn(false);
			const autoGeneratedRows = selectedRows.filter((el) => el.autoGenerated === true);
			const isAutoGenerated = autoGeneratedRows.length > 0;
			setDisabledDeleteBtn(isAutoGenerated);
			setDisabledExportBtn(false);
			setDisabledPushCohortsBtn(false);
			setDisabledPullCohortsBtn(false);
			setDisabledBulkTtlBtn(false);
		setDisabledBulkExportBtn(false);
		}
		if (readOnly) {
			setDisabledCopyBtn(true);
			setDisabledEditBtn(true);
			setDisabledArchiveBtn(true);
			setDisabledUnarchiveBtn(true);
			setDisabledDeleteBtn(true);
			setDisabledPushCohortsBtn(true);
			setDisabledPullCohortsBtn(true);
			setDisabledBulkTtlBtn(true);
		setDisabledBulkExportBtn(true);
		}
		// Publisher Contributor: editor (not readOnly) but barred from lifecycle/delivery
		// actions. Content actions (Copy, Edit, Update TTL, Export) stay enabled. See ASB-3200.
		if (!canManageLifecycle) {
			setDisabledDeleteBtn(true);
			setDisabledArchiveBtn(true);
			setDisabledUnarchiveBtn(true);
			setDisabledPushCohortsBtn(true);
			setDisabledPullCohortsBtn(true);
		}
	};

	const handleTab = (tab, i) => {
		setTab(tab);
		const { autoGenerated, audience_type } = tabFilters[tab] ?? tabFilters.All;
		setFilters((prevState) => ({
			...prevState,
			autoGenerated: { value: autoGenerated, matchMode: prevState.autoGenerated.matchMode },
			audience_type: { value: audience_type, matchMode: prevState.audience_type.matchMode },
		}));

		return tableState;
	};

	const handlePostDelete = () => {
		setCohortsToDelete(null);
		setShowConfirmation(false);
	};

	const handlePostUpdate = () => {
		setArchiveType(null);
		setShowArchiveEdit(false);
	};

	const handlePostCohortPush = () => {
		setCohortsToPush(null);
		setShowPushCohorts(false);
	}

	const handlePostCohortPull = () => {
		setCohortsToPull(null);
		setShowPullCohorts(false);
	}

	const handlePostCopy = () => {
		setShowCopy(false);
	};

	const handlePostBulkTtlEdit = () => {
		setCohortsToBulkTtlEdit(null);
		setShowBulkTtlEdit(false);
	};

	const handlePostBulkExport = () => {
		setCohortsToBulkExport(null);
		setShowBulkExport(false);
	};

	const handlePostBulkImport = () => {
		setShowBulkImport(false);
		// An import may have changed audiences in the list: reload it, so the next push, pull
		// or bulk edit sends their current versions (ASB-4801) and shows what the import did.
		tagCohorts(tagId);
	};

	return (
		<div
			id="audience-manager"
			className="wrapper"
		>
			<div className="top-fold w-tabs">
				<div className="container">
					<div className="container--inner">
						<h1>Audience Manager</h1>

						<div className="audiences">
							<div className="headings">
								<p className="p2 strong">Top Auto-Generated Audiences</p>
								<p className="p4">Top Audience (Unique Users)</p>
							</div>

							<TopAudiences />
						</div>

						{/* No numbers until the rows are in. `tableState` is null until the fetch
						    resolves and there is no failure branch on it (pre-existing, `:258`),
						    so counting an empty list here tells a publisher with 1,197 audiences
						    they have none -- during every load, and permanently if it fails. */}
						<p className="p2 strong audiences-count">
							Audiences{" "}
							{tableState && (
								<>
									({filteredCount.toLocaleString()} of{" "}
									{(tableState.length).toLocaleString()})
								</>
							)}
						</p>
					</div>
				</div>
			</div>
			<div className="tab-row container--inner">
				{tabs.map((tab, i) => (
					<button
						className={`tab-button ${currentTab == tab ? "active" : "inactive"}`}
						key={i}
						type="button"
						onClick={() => handleTab(tab)}
						hidden={(tab === 'Modeled' && !(amsFlavor === "full" && modelingEnabled)) || (tab === 'Signal' && !signalEnabled)}
					>
						{tab}
						{tableState && (
							<span className="tab-count"> ({(tabCounts[tab] ?? 0).toLocaleString()})</span>
						)}
					</button>
				))}
			</div>
			<div className="bottom-fold">
				{editToast && (
					<Toast
						status={editToast.status}
						title={editToast.title}
						message={editToast.message}
						callback={() => setEditToast(null)}
						autoHide={true}
					/>
				)}
				{showConfirmation && (
					<DeleteConfirmation
						cohort={cohortsToDelete}
						onUpdate={handlePostDelete}
						tagCohorts={setSelectedRows}
						getCohorts={tagCohorts}
					/>
				)}
				{showArchiveEdit && (
					<ArchiveConfirmation
						cohort={cohortsToUpdate}
						archiveType={archiveType}
						onUpdate={handlePostUpdate}
						selectedRows={setSelectedRows}
						getCohorts={tagCohorts}
					/>
				)}
				{showCopy && (
					<CopyCohort
						cohort={cohortsToUpdate}
						onUpdate={handlePostCopy}
						getCohorts={tagCohorts}
						selectedRows={setSelectedRows}
					/>
				)}
				{showPushCohorts && (
					<UpdateActivationMethods
						cohort={cohortsToPush}
						onUpdate={handlePostCohortPush}
						tagCohorts={setSelectedRows}
						getCohorts={tagCohorts}
						pullCohorts={false}
					/>
				)}
				{showPullCohorts && (
					<UpdateActivationMethods
						cohort={cohortsToPull}
						onUpdate={handlePostCohortPull}
						tagCohorts={setSelectedRows}
						getCohorts={tagCohorts}
						pullCohorts={true}
					/>
				)}
				{showBulkTtlEdit && (
					<BulkTtlEdit
						cohort={cohortsToBulkTtlEdit}
						onUpdate={handlePostBulkTtlEdit}
						tagCohorts={setSelectedRows}
						getCohorts={tagCohorts}
					/>
				)}
				{showBulkExport && (
					<BulkExport
						cohort={cohortsToBulkExport}
						onUpdate={handlePostBulkExport}
						tagCohorts={setSelectedRows}
					/>
				)}
				{showBulkImport && (
					<BulkImport
						onUpdate={handlePostBulkImport}
						getCohorts={tagCohorts}
					/>
				)}
				<div className="container">
					<div className="container--inner">
						<div className="filters">
							<SelectField
								label="Unique Users"
								name="unique_users"
								value={value.unique_users}
								onChange={() => setValue({ unique_users: selectedOptions.unique_users })}
								required={false}
								disabled={false}
								selected={selectedOptions.unique_users}
								placeholder="Select..."
								options={users}
								onSubmit={(value) => filter(value, "unique_users")}
								ref={usersRef}
							/>
							{tagData?.subdomain !== "silo60" && (
								<SelectField
									label="Prebid CPM"
									name="prebid_cpm"
									value={null}
									required={false}
									disabled={false}
									options={prebidCpmOptions}
									onSubmit={(value) => filter(value, "prebid_cpm")}
									ref={cpmRef}
								/>
							)}
							<SelectField
								label="Status"
								name="active"
								value={value.status}
								onChange={() => setValue({ active: selectedOptions.active })}
								required={false}
								selected={selectedOptions.active}
								placeholder="Select..."
								disabled={false}
								options={activeStatus.filter((e) => e.value === true || e.value === false || modelingEnabled).map((e) => ({value: e.label, label: e.label}))}
								onSubmit={(arr) => filter(arr, "active")}
								ref={statusRef}
							/>
							<SelectField
								label="Destination"
								name="destination"
								required={false}
								disabled={false}
								placeholder="Select..."
								options={destinationOptions}
								onSubmit={(opt) => setSelectedDestination(opt?.value ?? null)}
								ref={destinationRef}
							/>

							<button
								className="clear-filters"
								type="button"
								onClick={() => clearFilters()}
							>
								Clear filters
							</button>

							<BasicInput
								id="searchInput"
								name="audience_name"
								required={false}
								disabled={false}
								placeholder="Audience Name or ID"
								search={true}
								customClasses="filters-search"
								onInput={(e) => {
									setFilters((prevState) => ({
										...prevState,
										audience_name_search_box: { value: e.target.value, matchMode: FilterMatchMode.CONTAINS },
									}));
								}}
								ref={audienceRef}
							/>
						</div>
						<div className="table-head">
							<div className="table-head__actions">
								<div
									className="pop-menu-wrapper"
									ref={popRef}
								>
								<button
									className={`btn-simple ${selectedRows.length ? "enabled" : "disabled"}`}
									onClick={() => [
										setPopMenus((prev) => ({ ...prev, actions: !prev.actions })),
										handleActions(selectedRows),
									]}
								>
									Actions
									<img
										src={chev}
										alt="Actions"
									/>
								</button>

								{popMenus.actions && (
									<Popmenu
										items={[
											{
												type: "button",
												disabledBtn: disabledEditBtn,
												text: "Edit",
												active: false,
												callback: function () {
													const editPath = selectedAudienceType === "Modeled"
														? `/audience-modeling/edit/${audienceId}`
														: selectedAudienceType === "Signal"
														? `/audience-signal/edit/${audienceId}`
														: `/audience-builder-v2/edit/${audienceId}`;
													window.open(editPath, "_blank");
													setSelectedRows([]);
													setPopMenus((prev) => ({ ...prev, actions: !prev.actions }));
												},
											},
											{
												type: "button",
												disabledBtn: disabledViewBtn,
												text: "View",
												active: false,
												callback: function () {
													const viewPath = getViewPath({
														audience_type: selectedAudienceType,
														uniqueId: audienceId,
													});
													window.open(viewPath, "_blank");
													setSelectedRows([]);
													setPopMenus((prev) => ({ ...prev, actions: !prev.actions }));
												},
											},
											{
												type: "button",
												text: "Copy",
												disabledBtn: disabledCopyBtn,
												active: false,
												callback: function () {
													handleCopy();
													setPopMenus((prev) => ({ ...prev, actions: !prev.actions }));
												},
											},
											...((bulkEditEnabled || isAdmin) ? [{
												type: "button",
												text: "Export for Bulk Edit",
												disabledBtn: disabledBulkExportBtn,
												active: false,
												callback: function () {
													handleBulkExport();
													setPopMenus((prev) => ({ ...prev, actions: !prev.actions }));
												},
											}] : []),
											{
												type: "button",
												text: "Push to Destinations",
												disabledBtn: disabledPushCohortsBtn,
												active: false,
												callback: function () {
													handlePushCohorts();
													setPopMenus((prev) => ({ ...prev, actions: !prev.actions }));
												},
											},
											{
												type: "button",
												text: "Pull from Destinations",
												disabledBtn: disabledPullCohortsBtn,
												active: false,
												callback: function () {
													handlePullCohorts();
													setPopMenus((prev) => ({ ...prev, actions: !prev.actions }));
												},
											},
											{
												type: "button",
												text: "Delete",
												disabledBtn: disabledDeleteBtn,
												active: false,
												callback: function () {
													handleDelete();
													setPopMenus((prev) => ({ ...prev, actions: !prev.actions }));
												},
											},
											{
												type: "button",
												text: "Archive",
												disabledBtn: disabledArchiveBtn,
												active: false,
												callback: function () {
													handleArchive();
													setArchiveType("Archive");
													setPopMenus((prev) => ({ ...prev, actions: !prev.actions }));
												},
											},
											{
												type: "button",
												text: "Unarchive / Activate",
												disabledBtn: disabledUnarchiveBtn,
												active: false,
												callback: function () {
													setArchiveType("Unarchive");
													handleUnarchive();
													setPopMenus((prev) => ({ ...prev, actions: !prev.actions }));
												},
											},
											{
												type: "button",
												text: "Update TTL",
												disabledBtn: disabledBulkTtlBtn,
												active: false,
												callback: function () {
													handleBulkTtlEdit();
													setPopMenus((prev) => ({ ...prev, actions: !prev.actions }));
												},
											},
											{
												type: "export",
												text: "Export",
												disabledBtn: disabledExportBtn,
												active: false,
												callback: function () {
													setPopMenus((prev) => ({ ...prev, actions: !prev.actions }));
													setSelectedRows([]);
												},
												csvHeaders: [
													{ label: "Date Created", key: "date_created" },
													{ label: "ID", key: "uniqueId" },
													{ label: "Audience Name", key: "audience_name" },
													{ label: "Audience Type", key: "audience_type" },
													{ label: "Unique Users", key: "unique_users" },
													...(tagData?.subdomain !== "silo60" ? [
														{ label: "Prebid CPM", key: "prebid_cpm" },
														{ label: "Prebid Revenue", key: "prebid_revenue" },
													] : []),
													{ label: "Status", key: "active" }
												],
												csvData: selectedRows,
												csvFilename: `cohorts_${(new Date()).toISOString()}.csv`,
											}
										]}
									/>
								)}
							</div>
								{(bulkEditEnabled || isAdmin) && !readOnly && (
									<button
										className="btn-simple enabled"
										onClick={handleBulkImport}
									>
										Import Edits
									</button>
								)}
							</div>
						</div>
						<DataTable
							filters={filters}
							filterDisplay="rows"
							globalFilterFields={headers.map((el) => el[0])}
							loading={loading}
							value={displayedTableState}
							resizableColumns={true}
							columnResizeMode={"expand"}
							paginator
							paginatorTemplate={{
								layout: "FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown CurrentPageReport",
								CurrentPageReport: pageReport,
							}}
							rows={10}
							rowsPerPageOptions={[10,25,50]}
							selectionMode="checkbox"
							selection={selectedRows}
							emptyMessage="No Audience found."
							onSelectionChange={(e) => setSelectedRows(e.value)}
							dataKey="id"
							sortField={"unique_users"}
							sortOrder={-1}
						>
							<Column
								key={"check"}
								selectionMode="multiple"
								headerStyle={{ width: "3rem" }}
							></Column>

							<Column
								field="date_created"
								header={headerTemplate("Date Created")}
								sortable
								sortField="date_created_raw"
							></Column>

							<Column
								field="uniqueId"
								header={headerTemplate("ID")}
							></Column>

							<Column
								field="audience_name"
								header={headerTemplate("Audience Name")}
								sortable
								body={audienceNameBody}
								style={tagData?.subdomain === "silo60" ? { width: '60%', maxWidth: '690px' } : { width: '40%', maxWidth: '380px'}}
							></Column>
							<Column
								field="unique_users"
								header={headerTemplate(
									"Unique Users",
									"Number of unique users in audience (Trailing 30 days)"
								)}
								body={formatUniqueUsers}
								sortable
							></Column>
							{tagData?.subdomain !== "silo60" && (
								<Column
									field="prebid_cpm"
									header={headerTemplate(
										"Prebid CPM",
										"Average CPM from Prebid auction activity (Trailing 30 days)"
									)}
									body={formatPrebidCpm}
									sortable
								></Column>
							)}
							{tagData?.subdomain !== "silo60" && (
								<Column
									field="prebid_revenue"
									header={headerTemplate(
										"Prebid Revenue",
										"Total revenue generated from Prebid auction activity (Trailing 30 days)"
									)}
									body={formatPrebidRevenue}
									sortable
								></Column>
							)}
							{showDv360SlotColumn && (
								<Column
									field="dv360Slot"
									header={headerTemplate(
										"DV360 Slot",
										"Permanent Floodlight custom-variable slot (u-n) this audience is assigned to"
									)}
									body={(aud) => (aud.dv360Slot != null ? `u${aud.dv360Slot}` : "Unassigned")}
									sortable
								></Column>
							)}
							{showEquativPriceColumn && (
								<Column
									field="equativPrice"
									header={headerTemplate(
										"Equativ Price",
										"CPM price this audience activates at in Equativ."
									)}
									body={equativPriceBody}
									sortable
								></Column>
							)}
							<Column
								field="info"
							/>
						</DataTable>
						{/* Tooltips created down here to prevent them from being trapped within data table. */}
						{/* This was a consequence of adding resizable columns and corresponding CSS. */}
						<Tooltip id={`audience-name-${kebabCase("Audience Type")}`} />
						<Tooltip id={`audience-name-${kebabCase("Unique Users")}`} />
						<Tooltip id={`audience-name-${kebabCase("Prebid CPM")}`} />
						<Tooltip id={`audience-name-${kebabCase("Prebid Revenue")}`} />
						<Tooltip id={`audience-name-${kebabCase("DV360 Slot")}`} />
						<Tooltip id={`audience-name-${kebabCase("Equativ Price")}`} />
						<Tooltip id={`audience-name-${kebabCase("Status")}`} />
						<Tooltip id={`audience-name-${kebabCase("Propensity Score")}`} />
					</div>
				</div>
			</div>
		</div>
	);
}
